import { Accordion, AccordionProps, createStyles } from '@mantine/core';
import { keys, noop } from 'lodash/fp';
import React, { useState } from 'react';

import { StoreListing, useStoreSettings } from '@portals/api/partners';
import { CurrencyRow } from '@portals/framework';
import { PeriodEnum, ProductPricingModel } from '@portals/types';

import { StoreListingPersonalizedPricingEmptyState } from '../../../../components/store-listings/StoreListingPersonalizedPricingEmptyState';
import { useStoreListingPeriodPriceTitle } from '../../../../hooks/store-listings.hooks';
import { PaymentSettingsMapKeys } from '../../../../types/store-listings.types';
import { mapStoreListingPricesToPaymentSettings } from '../../../../utils/store-listings.utils';

interface PaymentTabProps {
  storeListing: StoreListing;
  paymentSettings: ReturnType<typeof mapStoreListingPricesToPaymentSettings>;
}

export function PaymentTab({ storeListing, paymentSettings }: PaymentTabProps) {
  const { classes } = useStyles();

  const storeSettings = useStoreSettings();
  const { getPriceTitle } = useStoreListingPeriodPriceTitle();

  const [accordionItems, setAccordionItems] = useState<PeriodEnum[]>([]);

  if (storeListing.product.pricing_model === ProductPricingModel.Personalized) {
    return <StoreListingPersonalizedPricingEmptyState />;
  }

  return (
    <Accordion<true>
      multiple
      styles={accordionStyles}
      value={accordionItems}
      onChange={(value: PeriodEnum[]) => setAccordionItems(value)}
    >
      {(keys(paymentSettings) as Array<PaymentSettingsMapKeys>).map(
        (period) => {
          return (
            <Accordion.Item
              value={period}
              key={period}
              data-testid="payment-tab-accordion-item"
            >
              <Accordion.Control>
                {getPriceTitle({
                  period,
                  paymentSettings,
                  product: storeListing.product,
                })}
              </Accordion.Control>
              <Accordion.Panel>
                {paymentSettings[period]?.prices.map((price) => (
                  <CurrencyRow
                    readOnly
                    key={price.currency}
                    className={classes.currencyRow}
                    value={price.amountInScu ?? undefined}
                    onChange={noop}
                    currencyCode={price.currency}
                    isDefaultCurrency={
                      storeSettings.data?.default_currency === price.currency
                    }
                  />
                ))}
              </Accordion.Panel>
            </Accordion.Item>
          );
        }
      )}
    </Accordion>
  );
}

const useStyles = createStyles(() => ({
  currencyRow: {
    '&:last-child': {
      borderBottom: 'none',
    },
  },
}));

const accordionStyles: AccordionProps['styles'] = (theme) => ({
  panel: {
    position: 'relative',
  },
  control: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: theme.white,
  },
});
